import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CATEGORIES, CATEGORY_KEYS, catMeta } from "@/lib/categories";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { ShieldAlert, Plus, Sparkles, ThumbsUp, Trash2, Zap, Pencil } from "lucide-react";
import { toast } from "sonner";

const empty = { category: "preco", trigger: "", acknowledge: "", isolate: "", argument: "", effectiveness: 0 };

export default function Objections() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("todas");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/objections").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function generate() {
    if (!form.trigger.trim()) return toast.error("Escreva primeiro a objeção.");
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/generate-response", { objection_text: form.trigger, category: form.category });
      setForm((f) => ({
        ...f,
        category: data.category || f.category,
        acknowledge: data.acknowledge || f.acknowledge,
        isolate: data.isolate || f.isolate,
        argument: data.argument || f.argument,
      }));
      toast.success("Resposta gerada pela IA");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha na IA");
    } finally { setGenerating(false); }
  }

  async function save() {
    if (!form.trigger.trim()) return toast.error("A objeção é obrigatória.");
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/objections/${editingId}`, form);
        toast.success("Objeção atualizada");
      } else {
        await api.post("/objections", form);
        toast.success("Objeção adicionada à biblioteca");
      }
      setOpen(false); setForm(empty); setEditingId(null); await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha ao guardar");
    } finally { setSaving(false); }
  }

  function openCreate() { setEditingId(null); setForm(empty); setOpen(true); }
  function openEdit(o) {
    setEditingId(o.id);
    setForm({ category: o.category, trigger: o.trigger, acknowledge: o.acknowledge || "", isolate: o.isolate || "", argument: o.argument || "", effectiveness: o.effectiveness || 0 });
    setOpen(true);
  }

  async function upvote(id) {
    const { data } = await api.post(`/objections/${id}/upvote`);
    setItems((prev) => prev.map((o) => (o.id === id ? data : o)).sort((a, b) => b.effectiveness - a.effectiveness));
  }
  async function remove(id) {
    await api.delete(`/objections/${id}`);
    setItems((prev) => prev.filter((o) => o.id !== id));
  }

  const filtered = filter === "todas" ? items : items.filter((o) => o.category === filter);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-2">
            <ShieldAlert className="w-7 h-7 text-[#F59E0B]" /> Biblioteca de Objeções
          </h1>
          <p className="text-[#94A3B8] text-sm mt-1">O conhecimento coletivo da equipa. Cada resposta melhora com os votos de eficácia.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(empty); } }}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} data-testid="add-objection-button" className="bg-[#2563EB] hover:bg-[#1D4ED8]">
              <Plus className="w-4 h-4 mr-1.5" /> Nova objeção
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#111827] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display">{editingId ? "Editar objeção & resposta" : "Adicionar objeção & resposta"}</DialogTitle>
            <DialogDescription className="text-[#94A3B8]">Defina a objeção e a resposta em 3 camadas, ou use a IA para preencher.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="cue-label text-[#64748B]">Categoria</label>
                <select value={form.category} onChange={set("category")} data-testid="objection-category-select" className="w-full mt-1 h-10 rounded-md bg-[#0B0F17] border border-white/10 px-3 text-sm">
                  {CATEGORY_KEYS.map((k) => <option key={k} value={k}>{CATEGORIES[k].label}</option>)}
                </select>
              </div>
              <div>
                <label className="cue-label text-[#64748B]">Objeção do cliente</label>
                <Textarea value={form.trigger} onChange={set("trigger")} data-testid="objection-trigger-input" placeholder="Ex: Está demasiado caro para nós agora." className="mt-1 bg-[#0B0F17] border-white/10 min-h-[60px] resize-none" />
              </div>
              <Button onClick={generate} disabled={generating} data-testid="ai-fill-objection-button" variant="outline" className="w-full border-[#2563EB]/40 bg-transparent text-[#2563EB] hover:bg-[#2563EB]/10">
                <Sparkles className="w-4 h-4 mr-1.5" /> {generating ? "A gerar…" : "Preencher resposta com IA"}
              </Button>
              {["acknowledge", "isolate", "argument"].map((k, i) => (
                <div key={k}>
                  <label className="cue-label text-[#64748B]">{["1. Reconhecer", "2. Isolar", "3. Argumento"][i]}</label>
                  <Textarea value={form[k]} onChange={set(k)} className="mt-1 bg-[#0B0F17] border-white/10 min-h-[54px] resize-none text-sm" />
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button onClick={save} disabled={saving} data-testid="save-objection-button" className="bg-[#10B981] hover:bg-[#059669]">
                {saving ? "A guardar…" : editingId ? "Guardar alterações" : "Guardar na biblioteca"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={() => setFilter("todas")} className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${filter === "todas" ? "bg-white text-[#0B0F17]" : "bg-[#111827] border border-white/10 text-[#94A3B8]"}`}>Todas</button>
        {CATEGORY_KEYS.map((k) => (
          <button key={k} onClick={() => setFilter(k)} className="px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors"
            style={filter === k ? { color: "#fff", backgroundColor: CATEGORIES[k].color, borderColor: CATEGORIES[k].color }
              : { color: CATEGORIES[k].color, backgroundColor: CATEGORIES[k].bg, borderColor: CATEGORIES[k].color + "40" }}>
            {CATEGORIES[k].short}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((o) => {
          const m = catMeta(o.category);
          return (
            <div key={o.id} className="rounded-2xl border border-white/10 bg-[#111827] p-5 flex flex-col">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ color: m.color, backgroundColor: m.bg }}>{m.short}</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => openEdit(o)} data-testid={`edit-objection-${o.id}`}><Pencil className="w-4 h-4 text-[#475569] hover:text-[#2563EB]" /></button>
                  <button onClick={() => remove(o.id)} data-testid={`delete-objection-${o.id}`}><Trash2 className="w-4 h-4 text-[#475569] hover:text-[#EF4444]" /></button>
                </div>
              </div>
              <p className="font-display font-semibold text-[#F8FAFC]">{o.trigger}</p>
              <div className="mt-3 space-y-2 flex-1">
                {[["Reconhecer", o.acknowledge], ["Isolar", o.isolate], ["Argumento", o.argument]].map(([t, v]) => v && (
                  <div key={t}>
                    <div className="cue-label text-[#64748B]">{t}</div>
                    <p className="text-xs text-[#94A3B8] leading-relaxed">{v}</p>
                  </div>
                ))}
              </div>
              <button onClick={() => upvote(o.id)} data-testid={`upvote-${o.id}`} className="mt-3 flex items-center gap-1.5 text-xs text-[#94A3B8] hover:text-[#F59E0B] transition-colors self-start">
                <Zap className="w-3.5 h-3.5" fill="#F59E0B" color="#F59E0B" /> Eficácia {o.effectiveness}
                <ThumbsUp className="w-3.5 h-3.5 ml-1" />
              </button>
            </div>
          );
        })}
      </div>
      {filtered.length === 0 && <p className="text-center text-[#64748B] py-12">Nenhuma objeção nesta categoria.</p>}
    </div>
  );
}
